import React, { useState } from "react";
import styled, { withTheme } from "styled-components";
import PrimaryButton from "../../../ui-library/Buttons/PrimaryButton/PrimaryButton";
import AlertUGNX from "../../../ToastsAndAlerts/AlertUGNX";
import { AlertType } from "../../../ToastsAndAlerts/constants";
import { useTranslation } from "react-i18next";
import { Row } from "react-bootstrap";
import Checkbox from "../../../ui-library/Checkbox/Checkbox";
import DateSelect from "../../../ui-library/DateAndTime/Date/DateSelect";
import { DateFormat } from "../../../../modules/constants";
import moment from "moment";

const Subtitle = styled.p`
  font-weight: 600;
  color: ${(props) => props.theme?.nero};
`;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  &.month-select {
    .react-datepicker__month-container > .react-datepicker__header,
    .react-datepicker__navigation {
      display: none;
    }
    .react-datepicker__month {
      margin: 1rem 0.4rem;
    }
  }
`;

const BirthDateContainer = styled.div`
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    .month-select {
      margin-right: 3rem;
    }
  }
`;

const SubtitleOpacity = styled.p`
  font-weight: 600;
  color: ${(props) => props.theme?.nero};
  opacity: 0.63;
`;

const BodyText = styled.p`
  color: ${(props) => props.theme?.nero};
`;

const CheckboxTitle = styled.span`
  color: ${(props) => props.theme?.nero};
  font-size: 16px;
  cursor: pointer;
  display: block;
  max-width: 280px;
`;

const Notice = styled.div`
  .title,
  .content {
    backface-visibility: hidden;
    transform: translateZ(0);
    transition: all 0.2s;
  }

  .title {
    color: ${(props) => props.theme?.fg};
    padding: 6px;
    border-bottom: 1px solid ${(props) => props.theme?.gray};
    display: block;
    background: ${(props) => props.theme?.whiteSmoke};

    i {
      position: absolute;
      right: 0;
      padding: 6px 12px;
    }
  }
  .title:after,
  .title:before {
    position: absolute;
    width: 2px;
    height: 0.75em;
    transition: all 0.2s;
  }
  .title:after {
    transform: rotate(90deg);
  }

  .content {
    max-height: 500px;
    overflow: hidden;
    p {
      margin: 0;
      font-size: 0.9em;
      line-height: 1.5;
      padding: 6px;
      white-space: pre-wrap;
      border-bottom: 1px solid ${(props) => props.theme?.gray};
    }
  }

  .toggle {
    visibility: hidden;
  }

  .toggle:checked {
    & + .title + .content {
      max-height: 0;
      border-bottom: 0;
    }
    & + .title:before {
      transform: rotate(90deg) !important;
    }
    & + .title i {
      transform: rotate(180deg) !important;
    }
  }
`;

const StyledButton = styled(PrimaryButton)`
  margin-top: 5px;
  margin-left: auto;
  display: flex;

  @media (max-width: 414px) {
    width: 100%;
    display: block;
  }
`;

const Introduction = (props) => {
  const { handleSubmit, errorMessage } = props;

  const { t } = useTranslation();

  const [userType, setUserType] = useState(null);
  const [monthOfBirth, setMonthOfBirth] = useState(null);
  const [yearOfBirth, setYearOfBirth] = useState(null);

  const [showErrorDate, setShowErrorDate] = useState(false);
  const [isValidDate, setIsValidDate] = useState(true);

  const errorHandling = {
    showErrorDate: showErrorDate,
    setShowErrorDate: setShowErrorDate,
    isValidDate: isValidDate,
    setIsValidDate: setIsValidDate,
  };

  const submitForm = (e) => {
    const params = {
      yearOfBirth: parseInt(moment(yearOfBirth).format(DateFormat.YEAR)),
      monthOfBirth: parseInt(
        moment(monthOfBirth).format(DateFormat.MONTH_NUMBER)
      ),
      isCaregiver: !!userType,
    };
    e.preventDefault();
    handleSubmit(params);
  };

  const userTypes = [
    t("appText.introduction.option.consent"),
    t("appText.introduction.option.assent"),
  ];

  return (
    <form
      onSubmit={submitForm}
      className="auth-form"
      data-testid="introdction-view"
    >
      <BodyText className="mb-4">{t("appText.introduction.welcome")}</BodyText>
      <Subtitle className="mb-3">
        {t("appText.introduction.whoIsJoining")}
      </Subtitle>
      {userTypes?.map((item, key) => (
        <Row className="mt-2 mx-auto" key={`key_${key}`}>
          <Checkbox
            data-testid={`checkbox_user_${key}`}
            checked={userType === key}
            icon={false}
            square={false}
            round={true}
            onClick={() => setUserType(key)}
          />
          <CheckboxTitle
            data-testid={`checkbox_title_role_${key}`}
            className="ml-3"
            onClick={() => setUserType(key)}
          >
            {item}
          </CheckboxTitle>
        </Row>
      ))}
      <Notice>
        <input type="checkbox" id="notice-toggle" className="toggle" />
        <label className="title" htmlFor="notice-toggle">
          {t("appText.signup.notice.label")}
          <i className="fas fa-chevron-up" />
        </label>
        <div className="content">
          <p>{t("appText.introduction.notice.body")}</p>
        </div>
      </Notice>
      <Subtitle className="mt-4 mb-1">
        {t("appText.introduction.birth.header")}
      </Subtitle>
      <SubtitleOpacity className="mb-3">
        {t("appText.introduction.label.form")}
      </SubtitleOpacity>
      <BirthDateContainer className="mb-5">
        <DatePickerContainer className="month-select">
          <p className="my-auto">{t("appText.introduction.input.month")}</p>
          <DateSelect
            inputTitle={t("appText.introduction.input.month")}
            eventDate={monthOfBirth}
            setEventDate={(date) => setMonthOfBirth(date)}
            dateFormat={DateFormat.MONTH}
            displayTitle={false}
            errorHandling={errorHandling}
            showMonthYearPicker
            showFullMonthYearPicker
          />
        </DatePickerContainer>
        <DatePickerContainer>
          <p className="my-auto">{t("appText.introduction.input.year")}</p>
          <DateSelect
            inputTitle={t("appText.introduction.input.year")}
            eventDate={yearOfBirth}
            setEventDate={(date) => setYearOfBirth(date)}
            showYearPicker
            dateFormat={DateFormat.YEAR_LOWER}
            displayTitle={false}
            errorHandling={errorHandling}
            maxDate={new Date()}
          />
        </DatePickerContainer>
      </BirthDateContainer>
      {errorMessage && (
        <AlertUGNX data-testid="error-message" type={AlertType.DANGER}>
          {errorMessage}
        </AlertUGNX>
      )}
      <StyledButton
        type="submit"
        className="btn-md mt-1 mt-md-2 mt-xl-5 btn-auth"
        disabled={!(userType !== null && monthOfBirth && yearOfBirth)}
      >
        {t("appText.introduction.send")}
      </StyledButton>
    </form>
  );
};

export default withTheme(Introduction);
